/**
 * Analytics: Google Analytics 4 through Firebase, only with the player's consent.
 *
 * Nothing runs until the player presses Accept on the cookie notice. Until then (and forever, after
 * Decline) the Analytics SDK isn't even downloaded, no Google cookie is set, and gesTrack() drops
 * every event. The choice is remembered in localStorage.
 *
 * It needs Firebase configured on the server (GES_FIREBASE_CONFIG, read by api.js from /api/meta)
 * with a "measurementId" (G-...) in it. Without one, there is no notice and nothing is tracked.
 *
 * What gets counted (Firebase console → Analytics):
 *   tab_open {tab}              which screens people use
 *   company_start               pressed "Found the company" / "Start now"
 *   month_reached {month}       how far people play: 1, 2, 3, 6, 12, 24, 36, 60, 120
 *   next_month / fast_forward   how people move time
 *   feedback_sent {rating}      from app/feedback.js
 * Google also records visits, countries, devices and time played on its own.
 *
 * Cloudflare Web Analytics (cookie-free visit counts) is separate: Cloudflare adds it at the edge
 * (see deploy/LAUNCH.md), so it isn't controlled from here. Analytics must never break the game:
 * everything is wrapped in try/catch.
 */
const GES_CONSENT_KEY = "ges.analyticsConsent"; // "granted" | "denied"
const GES_ANALYTICS_SDK = "https://www.gstatic.com/firebasejs/10.12.2/firebase-analytics-compat.js";
const GES_MONTH_MILESTONES = [1, 2, 3, 6, 12, 24, 36, 60, 120];
let gesAnalytics = null;
let gesAnalyticsLoading = false;
let gesAnalyticsQueue = [];

function gesConsent() {
  try { return localStorage.getItem(GES_CONSENT_KEY); } catch (e) { return null; }
}

/** Firebase loaded (api.js) and its config has a measurementId. */
async function gesAnalyticsAvailable() {
  try {
    const fb = await window.gesFirebase;
    const cfg = window.gesMeta && window.gesMeta.firebase;
    return !!(fb && cfg && cfg.measurementId && window.firebase);
  } catch (e) { return false; }
}

async function gesStartAnalytics() {
  try {
    if (gesAnalytics || gesAnalyticsLoading || gesConsent() !== "granted" || !(await gesAnalyticsAvailable())) return;
    gesAnalyticsLoading = true;
    const s = document.createElement("script");
    s.src = GES_ANALYTICS_SDK; s.async = true;
    s.onload = () => {
      try {
        gesAnalytics = window.firebase.analytics();
        const queued = gesAnalyticsQueue; gesAnalyticsQueue = [];
        queued.forEach(([name, params]) => gesTrack(name, params));
      } catch (e) { console.warn("Analytics could not start (the game is unaffected):", e.message); }
      gesAnalyticsLoading = false;
    };
    s.onerror = () => { gesAnalyticsLoading = false; gesAnalyticsQueue = []; }; // blocked or offline: do without
    document.head.appendChild(s);
  } catch (e) { gesAnalyticsLoading = false; }
}

/** Logs one event. Does nothing without consent; never throws. */
function gesTrack(name, params) {
  try {
    if (gesConsent() !== "granted") return;
    if (!gesAnalytics) {
      if (gesAnalyticsLoading && gesAnalyticsQueue.length < 50) gesAnalyticsQueue.push([name, params || {}]);
      return;
    }
    gesAnalytics.logEvent(name, params || {});
  } catch (e) { /* analytics must never break the game */ }
}

function gesSetConsent(choice) {
  try { localStorage.setItem(GES_CONSENT_KEY, choice); } catch (e) { /* private mode: asked again next visit */ }
  const bar = document.getElementById("ges-cookie-notice");
  if (bar) bar.remove();
  if (choice === "granted") gesStartAnalytics();
}

/** The small cookie notice, shown once until the player picks Accept or Decline. */
async function gesShowCookieNotice() {
  try {
    if (gesConsent() || document.getElementById("ges-cookie-notice") || !(await gesAnalyticsAvailable())) return;
    const bar = document.createElement("div");
    bar.id = "ges-cookie-notice";
    bar.className = "cookie-notice";
    bar.setAttribute("role", "region");
    bar.setAttribute("aria-label", "Cookie notice");
    bar.innerHTML = `<span class="cookie-notice-text">We use cookies to count players and improve the game. <a href="/privacy.html" target="_blank" rel="noopener">Privacy &amp; data</a></span>
      <span class="cookie-notice-actions"><button type="button" data-consent="denied">Decline</button><button type="button" class="primary" data-consent="granted">Accept</button></span>`;
    bar.addEventListener("click", (e) => {
      const b = e.target.closest && e.target.closest("[data-consent]");
      if (b) gesSetConsent(b.dataset.consent);
    });
    document.body.appendChild(bar);
  } catch (e) { /* no notice, no tracking */ }
}

/** Game events, read from the page itself so no other module has to know about analytics. */
function gesWatchGame() {
  document.addEventListener("click", (e) => {
    const t = e.target.closest && e.target.closest("[data-tab], #btn-next-month, [data-ff], #qs-go");
    if (!t) return;
    if (t.dataset.tab) gesTrack("tab_open", { tab: t.dataset.tab });
    else if (t.id === "btn-next-month") gesTrack("next_month");
    else if (t.dataset.ff) gesTrack("fast_forward", { months: Number(t.dataset.ff) });
    else if (t.id === "qs-go") gesTrack("company_start", { path: "quick" });
  }, true);
  const form = document.getElementById("onboarding-form");
  if (form) form.addEventListener("submit", () => gesTrack("company_start", { path: "full" }), true);
  const month = document.getElementById("kpi-month");
  if (month && typeof MutationObserver === "function") {
    let last = 0;
    new MutationObserver(() => {
      const m = parseInt(month.textContent, 10);
      if (m > last && GES_MONTH_MILESTONES.includes(m)) gesTrack("month_reached", { month: m });
      if (m) last = m;
    }).observe(month, { childList: true, characterData: true, subtree: true });
  }
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  window.gesTrack = gesTrack;
  window.gesSetAnalyticsConsent = gesSetConsent;
  try {
    gesWatchGame();
    if (gesConsent() === "granted") gesStartAnalytics();
    else gesShowCookieNotice();
  } catch (e) { /* analytics must never break the game */ }
}
